import { useEffect, useState } from 'react';
import { Button } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  getShipmentCases,
  type ShipmentCase,
} from 'common/src/api/shipment-cases';
import type { Shipment } from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert, EmptyState, LoadingState, StatusChip } from '../ui';
import {
  caseKinds,
  caseLabel,
  caseStatuses,
  isCaseOverdue,
} from '../../pages/shipment-cases/model';

// Cases (claims, observations, incidents) live in the Reclamos module; this
// tab only summarizes the ones for this shipment and links there.
const ShipmentCasesTab = ({
  shipment,
  onOpen,
}: {
  shipment: Pick<Shipment, 'id' | 'exporter_id' | 'season' | 'species_id'>;
  onOpen: (path: string) => void;
}) => {
  const { t, currentLanguage } = useTranslation('shipmentCases');
  const [cases, setCases] = useState<ShipmentCase[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setCases(null);
    setError(false);
    getShipmentCases(
      {
        exporter_id: shipment.exporter_id,
        season: shipment.season,
        species_id: shipment.species_id,
      },
      controller.signal
    )
      .then((rows) =>
        setCases(
          rows.filter(
            (c) =>
              c.shipment_id === shipment.id ||
              c.shipment_snapshot.id === shipment.id
          )
        )
      )
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      });
    return () => controller.abort();
  }, [shipment.id, shipment.exporter_id, shipment.season, shipment.species_id]);

  const locale = currentLanguage === 'en' ? 'en-US' : 'es-CL';
  const money = (n: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  const kilos = (n: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);
  const date = (iso: string) =>
    new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  const today = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const moduleLink = `/claims/details?shipment=${shipment.id}`;

  if (error)
    return (
      <div className='pt-4'>
        <Alert variant='danger'>
          {t('No se pudieron cargar los reclamos del despacho.')}
        </Alert>
      </div>
    );
  if (!cases)
    return (
      <div className='pt-4'>
        <LoadingState label={t('Cargando reclamos...')} />
      </div>
    );

  if (cases.length === 0)
    return (
      <div className='pt-4'>
        <EmptyState
          icon='tabler:message-report'
          title={t('Este despacho no tiene reclamos')}
          description={t(
            'Los reclamos, observaciones y siniestros se registran y gestionan en el módulo Reclamos.'
          )}
          action={
            <Button
              color='primary'
              startContent={<Icon icon='tabler:arrow-right' />}
              onPress={() => onOpen(moduleLink)}
            >
              {t('Ir a Reclamos')}
            </Button>
          }
        />
      </div>
    );

  const open = cases.filter((c) => c.status !== 'closed');
  // A box can be in several cases: count it once, like the Reclamos module.
  const kilosByBox = new Map<number, number>();
  for (const c of cases)
    for (const i of c.items)
      kilosByBox.set(
        i.source_box_id,
        Math.max(kilosByBox.get(i.source_box_id) || 0, Number(i.kilos))
      );
  const affectedKilos = [...kilosByBox.values()].reduce((n, k) => n + k, 0);
  const valued = cases.filter((c) => c.valuation);
  const estimatedLoss = valued.reduce(
    (sum, c) => sum + Number(c.valuation?.amount_usd || 0),
    0
  );

  return (
    <div className='flex flex-col gap-4 pt-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h4 className='text-sm font-semibold text-foreground'>
            {t('Resumen de reclamos')}
          </h4>
          <p className='mt-0.5 text-xs text-default-500'>
            {t('La gestión de cada caso se hace en el módulo Reclamos.')}
          </p>
        </div>
        <Button
          variant='flat'
          size='sm'
          endContent={<Icon icon='tabler:arrow-right' />}
          onPress={() => onOpen(moduleLink)}
        >
          {t('Ir a Reclamos')}
        </Button>
      </div>

      <div className='grid grid-cols-2 gap-3 rounded-xl bg-default-50 p-4 sm:grid-cols-4'>
        {[
          [t('Casos'), String(cases.length)],
          [t('Abiertos'), String(open.length)],
          [t('Kg afectados'), kilos(affectedKilos)],
          [t('Pérdida estimada'), valued.length ? money(estimatedLoss) : '—'],
        ].map(([label, value]) => (
          <div key={label}>
            <p className='text-xs uppercase tracking-wide text-default-500'>
              {label}
            </p>
            <p className='mt-1 text-lg font-semibold tabular-nums text-foreground'>
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className='divide-y divide-default-200 rounded-xl border border-default-200'>
        {cases.map((c) => {
          const caseKilos = c.items.reduce((n, i) => n + Number(i.kilos), 0);
          return (
            <div key={c.id} className='flex flex-col gap-1.5 p-4'>
              <div className='flex items-start justify-between gap-2'>
                <div className='flex min-w-0 flex-wrap items-center gap-2 pt-1'>
                  <p className='text-sm font-semibold text-foreground'>
                    {caseLabel(c)}
                  </p>
                  <span className='text-sm text-default-500'>
                    {t(caseKinds[c.kind])}
                  </span>
                  <StatusChip
                    tone={
                      isCaseOverdue(c, today)
                        ? 'danger'
                        : c.status === 'waiting'
                          ? 'warning'
                          : c.status === 'closed'
                            ? 'default'
                            : 'info'
                    }
                  >
                    {t(caseStatuses[c.status])}
                  </StatusChip>
                </div>
                <Button
                  size='sm'
                  variant='light'
                  className='shrink-0'
                  endContent={<Icon icon='tabler:external-link' />}
                  onPress={() => onOpen(`/claims/details?case=${c.id}`)}
                >
                  {t('Ver caso')}
                </Button>
              </div>
              <p className='text-[13px] text-default-600'>
                {c.reasons.map((reason) => t(reason)).join(' · ')}
              </p>
              {c.description ? (
                <p className='line-clamp-2 text-[13px] text-default-500'>
                  {c.description}
                </p>
              ) : null}
              <p className='text-xs text-default-500'>
                {[
                  `${t('Ingresado')} ${date(c.created_at)}`,
                  caseKilos ? `${kilos(caseKilos)} kg` : null,
                  c.valuation ? money(Number(c.valuation.amount_usd)) : null,
                  c.responsible
                    ? `${t('Responsable')}: ${t(c.responsible)}`
                    : null,
                  c.status !== 'closed' && c.waiting_parties.length
                    ? `${t('Esperando a')} ${c.waiting_parties.join(', ')}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ShipmentCasesTab;
